import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaTasks, FaChartLine, FaStickyNote, FaHome, FaSignOutAlt, FaUser, FaBars, FaCalendarAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { cn } from '../utils/cn';
import { FaCalendar } from 'react-icons/fa';

const Layout = ({ children, fullWidth, noContainer }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Close sidebar by default on calendar page
  useEffect(() => {
    if (location.pathname === '/calendar' || location.pathname === '/calendarv2') {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isCalendarPage = location.pathname === '/calendar' || location.pathname === '/calendarv2';

  // For calendar page, render only the content with no sidebar or button
  if (isCalendarPage) {
    return (
      <div className="flex min-h-screen w-full">
        <div className="flex-1 w-full">
          {children}
        </div>
      </div>
    );
  }

  // For all other pages, render the normal layout
  return (
    <div className="flex min-h-screen">
      <Button 
        variant="outline" 
        size="icon" 
        className="fixed top-4 left-4 z-50 md:hidden" 
        onClick={toggleSidebar}
      >
        <FaBars className="h-4 w-4" />
        <span className="sr-only">Toggle sidebar</span>
      </Button>
      
      <aside 
        className={cn(
          "w-64 bg-card border-r border-border transition-all duration-300 z-40",
          "fixed inset-y-0 left-0 md:relative",
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        <div className="text-xl font-bold p-5 text-center border-b border-border">
          OrganiseWise
        </div>
        
        <ul className="p-0 m-0 list-none">
          <li>
            <Link 
              to="/" 
              className={cn(
                "flex items-center text-foreground px-5 py-4 hover:bg-muted transition-colors",
                isActive('/') && "bg-muted font-medium"
              )}
            >
              <FaHome className="mr-3" /> Home
            </Link>
          </li>
          <li>
            <Link 
              to="/dashboard" 
              className={cn(
                "flex items-center text-foreground px-5 py-4 hover:bg-muted transition-colors",
                isActive('/dashboard') && "bg-muted font-medium"
              )}
            >
              <FaChartLine className="mr-3" /> Dashboard
            </Link>
          </li>
          <li>
            <Link 
              to="/tasks" 
              className={cn(
                "flex items-center text-foreground px-5 py-4 hover:bg-muted transition-colors",
                isActive('/tasks') && "bg-muted font-medium"
              )}
            >
              <FaTasks className="mr-3" /> Tasks
            </Link>
          </li>
          <li>
            <Link 
              to="/calendar" 
              className={cn(
                "flex items-center text-foreground px-5 py-4 hover:bg-muted transition-colors",
                isActive('/calendar') && "bg-muted font-medium"
              )}
            >
              <FaCalendarAlt className="mr-3" /> Calendar
            </Link>
          </li>
          <li>
            <Link 
              to="/notes" 
              className={cn(
                "flex items-center text-foreground px-5 py-4 hover:bg-muted transition-colors",
                isActive('/notes') && "bg-muted font-medium"
              )}
            >
              <FaStickyNote className="mr-3" /> Notes
            </Link>
          </li>
          <li>
            <Link 
              to="/planner" 
              className={cn(
                "flex items-center text-foreground px-5 py-4 hover:bg-muted transition-colors",
                isActive('/planner') && "bg-muted font-medium"
              )}
            >
              <FaCalendar className="mr-3" /> Planner
            </Link>
          </li>
        </ul>
        
        <div className="mt-auto p-5 border-t border-border">
          <div className="flex items-center mb-4">
            <FaUser className="mr-3" />
            <span>{user?.email || 'User'}</span>
          </div>
          <Button 
            variant="outline" 
            className="flex items-center w-full justify-start" 
            onClick={handleLogout}
          >
            <FaSignOutAlt className="mr-3" />
            Logout
          </Button>
        </div>
      </aside>
      
      {noContainer ? (
        <div className="flex-1">
          {children}
        </div>
      ) : (
        <div 
          className={cn(
            "flex-1 bg-background transition-all duration-300",
            sidebarOpen && "md:ml-0",
            fullWidth ? "p-0" : "p-5"
          )}
          onClick={() => sidebarOpen && setSidebarOpen(false)}
        >
          <div className={cn(
            "pt-14 md:pt-0"
          )}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout; 